import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiltersDrawer, FilterField } from 'components/darwin-ui/FiltersDrawer';
import { getReportsColumns } from 'pages/Reports/columns';
import {
  REPORT_TYPES,
  REPORT_FILE_TYPES,
  REPORT_FIELD_NAMES,
  REPORT_COLUMN_KEYS,
  ReportType,
  ReportFileType,
} from 'constants/reports';
import {
  useGetReportFilterOptions,
  useDownloadTransactionsReport,
} from 'api/services/reportsService';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useReportsFilters } from 'contexts/ReportsFiltersContext';
import { parseISO } from 'date-fns';
import { ErrorType } from 'types/genericTypes';
import { GenerateReportParams } from 'types/reportTypes';

// Helper function to convert date strings/objects to UTC ISO 8601 format
const formatDateToISO = (dateInput?: string | Date): string | undefined => {
  if (!dateInput) return undefined;

  try {
    let date: Date;

    // If it's already a Date object, use it directly
    if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      // It's a string, try to parse it
      try {
        date = parseISO(dateInput);
      } catch {
        date = new Date(dateInput);
      }
    }

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('formatDateToISO: Invalid date format:', dateInput);
      return undefined;
    }

    // Return UTC ISO 8601 format (what SQL Server expects)
    return date.toISOString();
  } catch (error) {
    console.warn('formatDateToISO: Failed to parse date:', dateInput, error);
    return undefined;
  }
};

interface GenerateReportDrawerProps {
  open: boolean;
  onClose: () => void;
  hasFilters: boolean;
}

interface ReportFormValues {
  reportType: ReportType;
  fileType: ReportFileType;
  includedColumns: string[];
  traceabilityId?: string;
  lotCode?: string;
  referenceDocument?: string;
}

// FSMA exclusivity fields
const FSMA_FIELDS: (keyof ReportFormValues)[] = [
  REPORT_FIELD_NAMES.TRACEABILITY_ID,
  REPORT_FIELD_NAMES.LOT_CODE,
  REPORT_FIELD_NAMES.REFERENCE_DOCUMENT,
];

const applyFsmaExclusivity = (
  prev: Partial<ReportFormValues>,
  changed: Partial<ReportFormValues>
) => {
  // Check if any FSMA field is being changed
  const changedFsmaKey = FSMA_FIELDS.find(key => key in changed);

  if (!changedFsmaKey) {
    // No FSMA field changed, just merge the changes
    return { ...prev, ...changed };
  }

  const newValue = changed[changedFsmaKey];
  const hasValue = newValue != null && newValue !== '';

  if (!hasValue) {
    // Field is being cleared, just apply the change without clearing others
    return { ...prev, ...changed };
  }

  // Field is being set to a value, clear other FSMA fields
  const cleared = FSMA_FIELDS.reduce((acc, key) => {
    if (key !== changedFsmaKey) acc[key] = undefined;
    return acc;
  }, {} as Partial<ReportFormValues>);

  return { ...prev, ...changed, ...cleared };
};

export const GenerateReportDrawer = ({
  open,
  onClose,
  hasFilters,
}: GenerateReportDrawerProps) => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const { filters, activeFiltersCount } = useReportsFilters();

  const [currentReportType, setCurrentReportType] = useState<ReportType>(
    REPORT_TYPES.PER_FILTERS
  );

  const [formValues, setFormValues] = useState<Partial<ReportFormValues>>({});

  // Reset business-specific state when drawer closes
  // (FiltersDrawer handles form reset, but we need to reset our local state)
  useEffect(() => {
    if (!open) {
      setCurrentReportType(REPORT_TYPES.PER_FILTERS);
      setFormValues({});
    }
  }, [open]);

  // Filters data
  const { data: filterOptions } = useGetReportFilterOptions({
    limit: 100,
  });

  const columnsOptions = useMemo(() => {
    const columns = getReportsColumns(t, []);

    return columns
      .filter(c => c.key !== REPORT_COLUMN_KEYS.ACTIONS)
      .map(c => ({
        label: c.title as string,
        value: c.key as string,
      }));
  }, [t]);

  const isFsmaDisabled = useMemo(() => {
    return (active: keyof ReportFormValues) =>
      FSMA_FIELDS.some(
        key => key !== active && formValues[key] && formValues[key] !== ''
      );
  }, [formValues]);

  const fields = useMemo<FilterField[]>(() => {
    return [
      {
        name: REPORT_FIELD_NAMES.REPORT_TYPE,
        label: t('reports.generate-report-drawer.fields.report-type.label'),
        type: 'select',
        options: [
          {
            label: t(
              'reports.generate-report-drawer.fields.report-type.per-filters'
            ),
            value: REPORT_TYPES.PER_FILTERS,
          },
          {
            label: t('reports.generate-report-drawer.fields.report-type.fsma'),
            value: REPORT_TYPES.FSMA,
          },
          {
            label: t('reports.generate-report-drawer.fields.report-type.eudr'),
            value: REPORT_TYPES.EUDR,
          },
        ],
        span: 2,
      },
      {
        name: REPORT_FIELD_NAMES.FILE_TYPE,
        label: t('reports.generate-report-drawer.fields.file-type.label'),
        type: 'select',
        options: [
          {
            label: t('reports.generate-report-drawer.fields.file-type.pdf'),
            value: REPORT_FILE_TYPES.PDF,
          },
          {
            label: t('reports.generate-report-drawer.fields.file-type.csv'),
            value: REPORT_FILE_TYPES.CSV,
          },
          {
            label: t('reports.generate-report-drawer.fields.file-type.excel'),
            value: REPORT_FILE_TYPES.EXCEL,
          },
        ],
        span: 2,
      },
      {
        name: REPORT_FIELD_NAMES.INCLUDED_COLUMNS,
        label: t(
          'reports.generate-report-drawer.fields.included-columns.label'
        ),
        type: 'select',
        mode: 'multiple',
        options: columnsOptions,
        span: 2,
      },
      ...(currentReportType === REPORT_TYPES.FSMA
        ? [
            {
              name: REPORT_FIELD_NAMES.TRACEABILITY_ID,
              label: t(
                'reports.generate-report-drawer.fields.traceability-id.label'
              ),
              type: 'select' as const,
              options: filterOptions?.data
                ?.filter(o => o.traceabilityId)
                .map(o => ({
                  label: o.traceabilityId!,
                  value: o.transactionId,
                })),
              disabled: isFsmaDisabled(REPORT_FIELD_NAMES.TRACEABILITY_ID),
              allowClear: true,
              showSearch: true,
              span: 2 as const,
            },
            {
              name: REPORT_FIELD_NAMES.LOT_CODE,
              label: t('reports.generate-report-drawer.fields.lot-code.label'),
              type: 'select' as const,
              options: filterOptions?.data
                ?.filter(o => o.lotCode)
                .map(o => ({
                  label: o.lotCode!,
                  value: o.transactionId,
                })),
              disabled: isFsmaDisabled(REPORT_FIELD_NAMES.LOT_CODE),
              allowClear: true,
              showSearch: true,
              span: 2 as const,
            },
            {
              name: REPORT_FIELD_NAMES.REFERENCE_DOCUMENT,
              label: t(
                'reports.generate-report-drawer.fields.reference-document-number.label'
              ),
              type: 'select' as const,
              options: filterOptions?.data
                ?.filter(o => o.refDocNumber)
                .map(o => ({
                  label: o.refDocNumber!,
                  value: o.transactionId,
                })),
              disabled: isFsmaDisabled(REPORT_FIELD_NAMES.REFERENCE_DOCUMENT),
              allowClear: true,
              showSearch: true,
              span: 2 as const,
            },
          ]
        : []),
    ];
  }, [
    t,
    columnsOptions,
    currentReportType,
    filterOptions?.data,
    isFsmaDisabled,
  ]);

  const initialValues: Partial<ReportFormValues> = {
    reportType: REPORT_TYPES.PER_FILTERS,
    fileType: REPORT_FILE_TYPES.PDF,
    includedColumns: columnsOptions.map(c => c.value),
  };

  const handleValuesChange = (
    changedValues: Partial<ReportFormValues>,
    allValues: Partial<ReportFormValues>
  ) => {
    if (changedValues.reportType)
      setCurrentReportType(changedValues.reportType);

    const newValues = applyFsmaExclusivity(allValues, changedValues);
    setFormValues(newValues);

    // Return the new values to update the form
    return newValues;
  };

  const isApplyDisabled = (values: Partial<ReportFormValues>) => {
    const {
      reportType,
      includedColumns,
      traceabilityId,
      lotCode,
      referenceDocument,
    } = values;

    if (!reportType || isPending) return true;
    if (reportType === REPORT_TYPES.EUDR) return true;

    if (reportType === REPORT_TYPES.PER_FILTERS) {
      return activeFiltersCount === 0;
    }

    if (reportType === REPORT_TYPES.FSMA) {
      const hasFsma = traceabilityId || lotCode || referenceDocument;
      if (!hasFsma) return true;
    }

    return !includedColumns || includedColumns.length === 0;
  };

  const { mutateAsync: downloadReport, isPending } =
    useDownloadTransactionsReport();

  const handleApply = async (values: ReportFormValues) => {
    try {
      if (values.reportType === REPORT_TYPES.PER_FILTERS) {
        const reportParams: GenerateReportParams = {
          format: values.fileType,
          columns: values.includedColumns,
          since: formatDateToISO(filters.since),
          until: formatDateToISO(filters.until),
          event: filters.event,
          product: filters.products,
          lotCode: filters.lotCode,
          refDocNumber: filters.refDocNumber,
          refDocType: filters.refDocType,
        };

        await downloadReport(reportParams);
      }

      if (values.reportType === REPORT_TYPES.FSMA) {
        await downloadReport({
          format: values.fileType,
          columns: values.includedColumns,
          transactionId:
            values.traceabilityId || values.lotCode || values.referenceDocument,
        });
      }

      openNotification(
        'Success',
        t('reports.generate-report-drawer.success'),
        'success'
      );
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('reports.generate-report-drawer.error'),
          'error'
        );
      }
    }
  };

  return (
    <FiltersDrawer<ReportFormValues>
      open={open}
      onClose={onClose}
      title={t('reports.generate-report-drawer.title')}
      subtitle={t('reports.generate-report-drawer.subtitle')}
      fields={fields}
      form={{ initialValues, onValuesChange: handleValuesChange }}
      behavior={{
        closeOnApply: false,
      }}
      actions={{
        apply: {
          onApply: handleApply,
          isApplyDisabled,
          text: t('reports.generate-report-drawer.download-button'),
          loading: isPending,
        },
        cancel: {
          text: t('reports.generate-report-drawer.cancel-button'),
        },
      }}
    />
  );
};
